/**
 * Curated palette of visually distinct avatar placeholder colors.
 * Used when a user has no profile photo — each user gets a unique color
 * assigned at account creation time.
 *
 * Colors are chosen to be:
 * - Visually distinct from each other
 * - Readable with white text overlay
 * - Harmonious in a multi-user list
 */
export const PROFILE_COLOR_PALETTE: readonly string[] = [
    '#e53935', // Red
    '#d81b60', // Pink
    '#8e24aa', // Purple
    '#5e35b1', // Deep Purple
    '#3949ab', // Indigo
    '#1e88e5', // Blue
    '#039be5', // Light Blue
    '#00acc1', // Cyan
    '#00897b', // Teal
    '#43a047', // Green
    '#7cb342', // Light Green
    '#c0ca33', // Lime
    '#f4511e', // Deep Orange
    '#6d4c41', // Brown
    '#546e7a', // Blue Grey
    '#fb8c00', // Orange
    '#f9a825', // Amber
    '#00bcd4', // Cyan Alt
    '#26a69a', // Teal Alt
    '#ab47bc', // Purple Alt
    '#ec407a', // Pink Alt
    '#42a5f5', // Blue Alt
    '#ef6c00', // Dark Orange
    '#2e7d32', // Dark Green
] as const;

/** Default avatar color if none is assigned */
export const DEFAULT_PROFILE_COLOR = '#00bcd4';

/**
 * Pick a random color from the palette that is not already in use.
 * If all colors are taken, picks a random one from the full palette.
 *
 * @param usedColors - Array of hex colors already assigned to other users
 * @returns A hex color string
 */
export function pickUniqueProfileColor(usedColors: string[] = []): string {
    const usedSet = new Set(usedColors.map(c => c.toLowerCase()));
    const available = PROFILE_COLOR_PALETTE.filter(c => !usedSet.has(c.toLowerCase()));

    if (available.length > 0) {
        return available[Math.floor(Math.random() * available.length)];
    }

    // All colors are in use — pick a random one from the full palette
    return PROFILE_COLOR_PALETTE[Math.floor(Math.random() * PROFILE_COLOR_PALETTE.length)];
}
